// This provider is only used in client-side context

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { FeatureAccess, UserPlan, UserSubscription } from '../types';

interface FeatureContextType {
  features: FeatureAccess[];
  userPlan: UserPlan | null;
  subscription: UserSubscription | null;
  loading: boolean;
  error: string | null;

  // Feature methods
  checkAccess: (featureTag: string) => boolean;
  getFeature: (featureTag: string) => FeatureAccess | undefined;
  hasFeature: (featureTag: string) => boolean;
  refreshFeatures: () => Promise<void>;
}

const FeatureContext = createContext<FeatureContextType | null>(null);

interface FeatureProviderProps {
  children: ReactNode;
  appId: string;
  publicKey: string;
  apiHost: string;
  cacheTimeout?: number;
  userId?: string | null | (() => string | null | Promise<string | null>);
}

export function FeatureProvider({
  children,
  appId,
  publicKey,
  apiHost,
  cacheTimeout = 5 * 60 * 1000,
  userId
}: FeatureProviderProps) {
  const [features, setFeatures] = useState<FeatureAccess[]>([]);
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load features from API
  const loadFeatures = useCallback(async () => {
    // Resolve userId if it's a function
    let resolvedUserId: string | null = null;
    if (typeof userId === 'function') {
      resolvedUserId = await userId();
    } else {
      resolvedUserId = userId || null;
    }

    if (!resolvedUserId) {
      setFeatures([]);
      setUserPlan(null);
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setError(null);

      const response = await fetch(`${apiHost}/api/features/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicKey}`,
          'X-App-Id': appId
        },
        body: JSON.stringify({
          userId: resolvedUserId,
          appId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        const featuresArray = Array.isArray(data.features) ? data.features : [];
        setFeatures(featuresArray);
        setUserPlan(data.plan);
        setSubscription(data.subscription);

        // Cache for offline support
        localStorage.setItem('usecelo_features_cache', JSON.stringify({
          features: featuresArray,
          plan: data.plan,
          subscription: data.subscription,
          userId: resolvedUserId,
          timestamp: Date.now(),
        }));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load features';
      setError(errorMessage);
      console.error('UseCelo: Failed to load features:', err);

      // Try to load from cache
      try {
        const cached = localStorage.getItem('usecelo_features_cache');
        if (cached) {
          const { features: cachedFeatures, plan, subscription: cachedSub, userId, timestamp } = JSON.parse(cached);
          // Only use cache if it's for the same user and not too old
          if (userId === resolvedUserId && Date.now() - timestamp < cacheTimeout) {
            setFeatures(Array.isArray(cachedFeatures) ? cachedFeatures : []);
            setUserPlan(plan);
            setSubscription(cachedSub);
            console.info('UseCelo: Using cached features');
          }
        }
      } catch (cacheError) {
        console.error('UseCelo: Failed to load cached features:', cacheError);
      }
    } finally {
      setLoading(false);
    }
  }, [appId, publicKey, apiHost, userId, cacheTimeout]);

  // Feature checking methods
  const checkAccess = useCallback((featureTag: string): boolean => {
    return features.some(f => f.tag === featureTag && f.hasAccess);
  }, [features]);

  const getFeature = useCallback((featureTag: string): FeatureAccess | undefined => {
    return features.find(f => f.tag === featureTag);
  }, [features]);

  const hasFeature = useCallback((featureTag: string): boolean => {
    return checkAccess(featureTag);
  }, [checkAccess]);

  // Effects
  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  const contextValue: FeatureContextType = {
    features,
    userPlan,
    subscription,
    loading,
    error,
    checkAccess,
    getFeature,
    hasFeature,
    refreshFeatures: loadFeatures
  };

  return (
    <FeatureContext.Provider value={contextValue}>
      {children}
    </FeatureContext.Provider>
  );
}

export function useFeatures() {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatures must be used within FeatureProvider');
  }
  return context;
}